// src/pages/CustomChartsWidget.jsx
import React, { useEffect, useState } from "react";
import { Stack, Typography, Chip, Box, Divider, IconButton } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { request } from "../../utils/request";
import ChartRenderer from "./ChartRenderer";
import { enterpriseChartTheme } from "./chartTheme";

const CustomChartsWidget = () => {
  const [charts, setCharts] = useState([]);
  const [data, setData] = useState([]);
  const [chartType, setChartType] = useState("bar");

  useEffect(() => {
    const load = async () => {
      try {
        const res = await request("customcharts", { method: "GET" });
        const saved = Array.isArray(res?.data) ? res.data : [];
        setCharts(saved);
        
        if (saved[0]) {
          setChartType(saved[0].chartType || "bar");
          const preview = await request("customcharts/preview", {
            method: "POST",
            body: { 
              ...saved[0], 
              filters: Array.isArray(saved[0].filters) ? saved[0].filters : [] 
            },
          });
          setData(Array.isArray(preview?.data) ? preview.data : []);
        } else {
          setData([]);
        }
      } catch (error) {
        console.error(error);
      }
    };
    load();
  }, []);

  return (
    <div className="widget-card" style={{ width: "100%", height: "100%" }}>
      <div 
        className="global-card" 
        style={{ 
          display: "flex", 
          flexDirection: "column", 
          height: "100%", 
          padding: "1.25rem",
          overflow: "hidden" 
        }}
      >
        {/* Header Section */}
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1} sx={{ flexShrink: 0 }}>
          {/* 👇 FIXED: Replaced Typography with standard h5 tag to match other widgets perfectly */}
          <h5 className="global-section-title" style={{ margin: 0, textTransform: "capitalize" }}>
            {charts[0]?.title || "Insight Builder"}
          </h5>
          <Chip 
            label="Active" 
            size="small" 
            className="global-chip signal-secure-bg text-white" 
          />
        </Stack>
        <Typography variant="body2" className="text-secondary" sx={{ mb: 2, lineHeight: 1.5, flexShrink: 0 }}>
          {charts[0]?.description || "Create custom charts and visualizations to gain insights into your data."}
        </Typography>

        <Divider sx={{ borderColor: 'var(--hairline)', mb: 2, flexShrink: 0 }} />

        {/* Main Content Area */}
        <Stack spacing={1.5} sx={{ flex: 1, minHeight: 0 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography 
              variant="caption" 
              className="text-secondary"
              sx={{ fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" }}
            >
              Dashboard widget
            </Typography>
            <Chip 
              label={`${charts.length} saved`} 
              size="small" 
              className="global-chip bg-blue-tint text-interactive"
              sx={{ borderRadius: 999, m: 0 }} 
            />
          </Stack>

          {data.length ? (
            <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
              <ChartRenderer 
                data={data} 
                chartType={chartType} 
                xKey="name" 
                yKey="value" 
                xLabel={charts[0]?.xLabel}
                yLabel={charts[0]?.yLabel}
                colors={charts[0]?.appearance?.primaryColor ? [charts[0].appearance.primaryColor] : [enterpriseChartTheme.colors[0]]} 
              />
            </Box>
          ) : (
            <Box
              className="bg-mist text-secondary"
              sx={{
                flex: 1,
                minHeight: 0,
                border: "1px dashed var(--hairline)",
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                px: 2,
              }}
            >
              <Typography variant="body2" color="inherit">
                Create a chart to see it appear here.
              </Typography>
            </Box>
          )}
        </Stack>

        <Divider sx={{ borderColor: 'var(--hairline)', mt: 1.5, mb: 1.5, flexShrink: 0 }} />

        {/* Footer Section */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ flexShrink: 0 }}>
          <Typography variant="caption" className="text-secondary">
            Enterprise analytics
          </Typography>
          <IconButton 
            size="small" 
            onClick={() => window.location.reload()} 
            className="global-icon-button"
            sx={{ p: 0.5 }}
          >
            <RefreshIcon fontSize="small" className="text-secondary" />
          </IconButton>
        </Stack>
      </div>
    </div>
  );
};

export default CustomChartsWidget;